import { type ReactNode, type Ref, useLayoutEffect, useRef } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import type { SourceOption } from '../_lib/source-option'
import { SourceGlyph } from '../shared/source-glyph'
import {
  SourceCountBadge,
  SourceOptionButton,
} from '../shared/source-option-button'

/**
 * The row's source identity mark, pinned ahead of the external id on the
 * title (LG-362 §3 revision): one mark for the source the row currently
 * wears, a count badge for how many *other* places list it, and — on hover
 * or keyboard focus — the rest of the sources, each a "show as this source"
 * button. Picking is a display swap only; nothing is staged (the §2 add-on-
 * click semantics were a mistake). The row's +Add is the sole add path and
 * stages whatever identity the row wears.
 *
 * The worn front is resolved by the caller (`wornSource` in browse-table) so
 * the pick → owning → first chain exists once; this component only renders
 * it. The front mark is never a button (the inline-switcher doctrine: the
 * current source is never a target) — clicking it must not convert the
 * implicit null pick into an explicit one, which would silently pin the
 * staged source. It stays keyboard-focusable so `Tab` expands the stack.
 *
 * Resting state is deliberately a single mark: 231 of the 302 published
 * problems carry a second source, so an always-expanded stack would repeat
 * the worn logo down the whole column and say nothing. The badge carries
 * the only per-row information the resting state needs — that alternatives
 * exist, and how many.
 *
 * Expansion is CSS only (`group-hover` / `focus-within`), so the buttons are
 * always real, focusable DOM in source order; no JS hover tracking, no
 * aria-expanded state machine. The badge is presentational (`aria-hidden`):
 * the mark and buttons name every source.
 *
 * A row with no options — a coding exercise, a mock-mode row — renders
 * nothing: there is no per-source identity to show or switch
 * (`CodingExerciseSource` has no analogue of `CoachingAssignment.sourceId`).
 */

function sourceLabel(option: SourceOption): string {
  return option.externalId === null
    ? option.displayName
    : `${option.displayName} · ${option.externalId}`
}

function FrontMark({
  option,
  label,
  className = '',
  ref,
}: {
  option: SourceOption
  label: string
  className?: string
  ref?: Ref<HTMLSpanElement>
}): ReactNode {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          ref={ref}
          role="img"
          // biome-ignore lint/a11y/noNoninteractiveTabindex: Informational tooltip triggers must be keyboard focusable (the browse-table rule), and focusing the mark expands the stack.
          tabIndex={0}
          aria-label={label}
          className={`block ${className}`}
        >
          <SourceGlyph displayName={option.displayName} icon={option.icon} />
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

export function SourceCell({
  sources,
  worn,
  onSelect,
  className = '',
}: {
  /** Owning first — the reader's sort is the contract, not re-sorted here. */
  sources: readonly SourceOption[]
  /** The identity the row wears, resolved by the caller; null renders nothing. */
  worn: SourceOption | null
  onSelect: (sourceId: string) => void
  className?: string
}): ReactNode {
  const frontMarkRef = useRef<HTMLSpanElement>(null)
  // Picking unmounts the very button that was activated (its source becomes
  // the worn front, leaving `rest`), which would drop focus to <body> and
  // collapse the focus-within stack — the keyboard user is thrown out of the
  // row with no way to verify or undo the pick. Same shape as column-menu's
  // pendingReorderFocus: flag the pick, then put focus on the new front mark
  // after React commits (which also keeps the stack expanded).
  const pendingPickFocus = useRef(false)
  useLayoutEffect(() => {
    if (!pendingPickFocus.current) return
    pendingPickFocus.current = false
    frontMarkRef.current?.focus()
  })

  if (worn === null || sources.length === 0) return null

  // By sourceId, never object identity: `worn` is resolved by the caller, and
  // a caller that maps or clones its options must not see the worn source
  // repeated as a no-op "Show as" button.
  const rest = sources.filter((option) => option.sourceId !== worn.sourceId)

  if (rest.length === 0) {
    // A single source offers no choice — just the named mark.
    return (
      <FrontMark
        option={worn}
        label={`Source ${sourceLabel(worn)}`}
        className={className}
      />
    )
  }

  return (
    <span className={`group/source flex items-center ${className}`}>
      <span className="relative">
        <FrontMark
          ref={frontMarkRef}
          option={worn}
          label={`Showing ${sourceLabel(worn)}`}
        />
        <SourceCountBadge
          count={rest.length}
          className="group-focus-within/source:opacity-0 group-hover/source:opacity-0 [@media(hover:none)]:opacity-0"
        />
      </span>
      {/* The `(hover: none)` escape is the reveal-on-hover.ts rule: Tailwind
          compiles `group-hover:` inside `@media (hover: hover)`, so without it
          a touch device gets a stack that no gesture can ever expand. On touch
          the alternates are simply always visible (and the badge, which only
          announces them, never is). */}
      <span className="flex max-w-0 items-center gap-1 overflow-hidden opacity-0 transition-all duration-150 group-focus-within/source:max-w-24 group-focus-within/source:overflow-visible group-focus-within/source:pl-1 group-focus-within/source:opacity-100 group-hover/source:max-w-24 group-hover/source:overflow-visible group-hover/source:pl-1 group-hover/source:opacity-100 [@media(hover:none)]:max-w-24 [@media(hover:none)]:overflow-visible [@media(hover:none)]:pl-1 [@media(hover:none)]:opacity-100">
        {rest.map((option) => (
          <SourceOptionButton
            key={option.sourceId}
            option={option}
            label={`Show as ${sourceLabel(option)}`}
            tooltip={`Show as ${sourceLabel(option)} in the library`}
            disabled={false}
            onClick={() => {
              pendingPickFocus.current = true
              onSelect(option.sourceId)
            }}
          />
        ))}
      </span>
    </span>
  )
}
